'use client';
import { Children, cloneElement, isValidElement, ReactNode, useEffect, useRef, useState } from 'react';
import BulletList from '@tiptap/extension-bullet-list'
import Bold from '@tiptap/extension-bold'
import Strike from '@tiptap/extension-strike'
import Italic from '@tiptap/extension-italic'
import Document from '@tiptap/extension-document'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Placeholder from '@tiptap/extension-placeholder';
import Blockquote from '@tiptap/extension-blockquote';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Underline from '@tiptap/extension-underline';
import Typography from '@tiptap/extension-typography';
import History from '@tiptap/extension-history';
import { Link } from '@tiptap/extension-link';
import { Image } from '@tiptap/extension-image';
import { Youtube } from '@tiptap/extension-youtube';
import CustomMention from './CustomMention';
import userSuggestion from './userSuggestion';
import CustomHashtag from './CustomHashtag';
import hashtagSuggestion from './hashtagSuggestion';
import { EditorContent, Editor as TipTapEditor, useEditor } from '@tiptap/react';
import 'tippy.js/dist/tippy.css';
import MenuBar from './MenuBar';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { Type, ImagePlus } from 'lucide-react';

interface TextEditorProps {
  value: string;
  onChange: (content: string) => void;
  editorType: 'comment' | 'entity';
  placeholder?: ReactNode | string;
  fieldRequired?: boolean;
  children?: ReactNode;
  action?: 'edit' | 'reply' | 'add' | 'form'
  onCancel?: boolean;
  isEditorExpanded?: boolean;
}

const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  editorType,
  placeholder,
  fieldRequired = false,
  children,
  action = 'form',
  onCancel,
  isEditorExpanded,
}) => {
  const [editorLoaded, setEditorLoaded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [textExists, setTextExists] = useState(false);
  const [isTypeButtonActive, setIsTypeButtonActive] = useState(false);
  const [isExpanded, setIsExpanded] = useState(action === 'edit' || action === 'reply' || false);
  const [showExtensions, setShowExtensions] = useState(false);

  // Handle editor expansion state
  useEffect(() => {
    if (action === 'add') {
      setIsExpanded(true);
    } else {
      setIsExpanded(true);
    }
  }, [isFocused, textExists, showExtensions, isExpanded, action, isEditorExpanded]);

  const editor = useEditor({
    extensions: [
      Document,
      Bold,
      Italic,
      Strike,
      ListItem,
      History,
      OrderedList.configure({
        HTMLAttributes: {
          class: 'list-decimal pl-6 my-2',
        },
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: 'list-disc pl-6 my-2',
        },
      }),
      Text,
      Paragraph,
      Blockquote.configure({
        HTMLAttributes: {
          class: 'p-4 my-2 bg-eoe-icon dark:bg-eoe-dark-icon border-l-4 border-gray-300 dark:border-eoe-dark-text-heading text-base italic leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading',
          p: 'text-xl italic font-bold leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading',
        },
      }),
      HardBreak,
      Heading,
      Placeholder.configure({
        placeholder: editorType === 'comment' && !textExists && action === 'add' ? 'Join the discussion' : '',
        showOnlyCurrent: true,
        emptyEditorClass: `cursor-text before:text-eoe-text-meta before:dark:text-eoe-dark-text-meta before:absolute before:text-base ${
          isFocused ? 'before:hidden' : 'before:content-[attr(data-placeholder)] after:hidden'
        } ${fieldRequired ? 'after:text-warning-red after:dark:text-dark-warning-red after:ml-[82px] after:absolute after:bottom-[4px]' : ''}`,
      }),
      Underline,
      Typography,
      Link.configure({
        autolink: true,
        linkOnPaste: true,
        openOnClick: true,
        defaultProtocol: 'https',
        HTMLAttributes: {
          class: 'link external-link line-clamp-2',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-[100%]',
        },
        inline: true,
        allowBase64: true
      }),
      Youtube.configure({
        width: 256,
        height: 144,
      }),
      CustomMention.configure({
        HTMLAttributes: {
          class: 'mention',
        },
        suggestion: userSuggestion,
      }),
      CustomHashtag.configure({
        HTMLAttributes: {
          class: 'hashtag',
        },
        suggestion: hashtagSuggestion,
      }),
    ],
    autofocus: (action === 'reply' || action === 'edit'),
    editorProps: {
      attributes: {
        class: `flex flex-col px-3 py-2 text-wrap justify-start items-start w-full gap-x-3 prose-wrapper focus:outline-none`,
      },
      handleDOMEvents: {
        focus: () => {
          setIsFocused(true);
          return false;
        },
        blur: () => {
          if (action === 'edit' || action === 'reply') {
            setIsFocused(false);
            return false;
          }
          setIsFocused(false);
          return true;
        },
      },
    },
    content: value || ``,
    onCreate: () => {
      setEditorLoaded(true);
      if (action === 'edit' || action === 'reply') {
        setTimeout(() => {
          editor?.commands.focus('end');
        }, 0);
      }
    },
    onUpdate({ editor }) {
      const content = editor.getHTML();
      onChange(content);
      setTextExists(editor.getText().length > 0);
    },
  });

  const editorRef = useRef<TipTapEditor | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editor) {
      editorRef.current = editor;
    }
  }, [editor]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
      if (value.length > 0) {
        setTextExists(true);
      }
    }
  }, [value, editor]);
  // Handle text content changes
  useEffect(() => {
    if (editor) {
      const content = editor.getText();
      const hasText = content.length > 0;
      setIsExpanded(true);
      setTextExists(hasText);
    }
  }, [editor]);

  // Update text existence when editor content changes
  useEffect(() => {
    const handleUpdate = () => {
      if (editor) {
        const hasText = editor.getText().length > 0;
        setTextExists(hasText);
      }
    };

    if (editor) {
      editor.on('update', handleUpdate);
    }

    return () => {
      if (editor) {
        editor.off('update', handleUpdate);
      }
    };
  }, [editor]);

  // Handle MenuBar visibility
  useEffect(() => {
    setShowExtensions(isTypeButtonActive);
    setIsExpanded(isExpanded);
  }, [isTypeButtonActive, isExpanded]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !editor) return;

    if (!file.type.startsWith('image/')) {
      console.error('Uploaded file is not an image');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        editor.chain().focus().setImage({ src: result }).run();
      }
    };
    reader.readAsDataURL(file);
    
    // Reset the input value so the same file can be uploaded again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return !editorLoaded ? (
    <LoadingSkeleton count={1} height={`${action === 'add' ? 'h-8' : 'h-20'}`} rounded={`${action === 'add' ? 'rounded-full' : 'rounded-2xl'}`} width='w-full' margin='mx-0'/>
  ) : (action === 'add' ? (
    <div 
      className={`editor-container text-sm 
        border
        text-gray-800 dark:text-eoe-dark-text
        transition-colors ${isExpanded ? 'overflow-y-hidden overflow-x-hidden' : ''}
        ${isExpanded && (isFocused) ? 'rounded-2xl border-[#666666] dark:border-white bg-transparent' : 'bg-gray-200 dark:bg-eoe-dark-input border-eoe-border-main dark:border-eoe-dark-border-main '}
        ${(!isExpanded && action === 'add') ? 'rounded-full max-h-8' : !isEditorExpanded && action === 'add' ? 'rounded-full max-h-8' : 'rounded-2xl'}`}
    >
    {showExtensions && <MenuBar editor={editor} editorType={editorType} />}
    
    {editorType !== 'comment' && (
      <span className={`relative block left-3 text-eoe-text-meta dark:text-eoe-dark-text-meta ${editor?.isFocused ? 'top-2 text-xs' : 'top-3 text-base'} transition-all`}
        onClick={() => {
          setIsFocused(true);
          editor?.commands.focus();
        }}
      >
        {placeholder}
        {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
      </span>
    )}
    
    <EditorContent editor={editor} className="prose-wrapper flex-1 transition-all" />
    
    <div className={`editor-actions flex-row flex-wrap my-2 gap-2 ${!isExpanded && !isFocused ? 'hidden' : 'flex'}`}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsFocused(true);
          setIsTypeButtonActive(!isTypeButtonActive);
          editor?.commands.focus();
        }}
        className={`ml-3 w-fit ${isTypeButtonActive ? 'rounded-full bg-eoe-icon-hover dark:bg-eoe-gray p-1' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta px-1'}`}
      >
        <Type className="size-4" />
      </button>
      
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        className="w-fit text-eoe-text-meta dark:text-eoe-dark-text-meta px-1"
        title="Upload image"
      >
        <ImagePlus className="size-4" />
      </button>
      
      <input 
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        className="hidden"
      />
      
      {children && Children.map(children, (child: ReactNode) => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            ...child.props
          });
        }
        return child;
      })}
    </div>
  </div>) : (<div 
    className={`editor-container text-sm 
      border
      text-gray-800 dark:text-eoe-dark-text
      transition-colors ${isExpanded ? 'overflow-y-hidden overflow-x-hidden' : ''}
      ${isExpanded && (isFocused) ? 'rounded-2xl border-[#666666] dark:border-white  bg-transparent' : 'bg-gray-200 dark:bg-eoe-dark-input border-eoe-border-main dark:border-eoe-dark-border-main '}
       rounded-2xl`}
  >
    {showExtensions && <MenuBar editor={editor} editorType={editorType} />}
    
    {editorType !== 'comment' && (
      <span className={`relative block left-3 text-eoe-text-meta dark:text-eoe-dark-text-meta ${isFocused ? 'top-2 text-xs' : 'top-3 text-base'} transition-all`}
        onClick={() => {
          setIsFocused(true);
          editor?.commands.focus();
        }}
      >
        {placeholder}
        {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
      </span>
    )}
    
    <EditorContent editor={editor} className="prose-wrapper flex-1 transition-all" />
    
    <div className={`editor-actions flex-col my-2 justify-start items-start gap-2 ${!isExpanded && !isFocused ? 'hidden' : 'flex'}`}>
      <div className="flex flex-row gap-2 ml-3">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsFocused(true);
            setIsTypeButtonActive(!isTypeButtonActive);
            editor?.commands.focus();
          }}
          className={`${isTypeButtonActive ? 'rounded-full bg-eoe-icon-hover dark:bg-eoe-gray p-1' : 'text-eoe-text-meta dark:text-eoe-dark-text-meta px-1'}`}
        >
          <Type className="size-4" />
        </button>
        
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          className="text-eoe-text-meta dark:text-eoe-dark-text-meta px-1"
          title="Upload image"
        >
          <ImagePlus className="size-4" />
        </button>
        
        <input 
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          className="hidden"
        />
      </div>
      
      {children && Children.map(children, (child: ReactNode) => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            ...child.props
          });
        }
        return child;
      })}
    </div>
  </div>
  ));
};

const styles = `
.ProseMirror {
  width: 100%;
}

.prose-wrapper {
  width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.prose-p {
  width: 100%;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}

.ProseMirror p {
  width: 100%;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
}
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default TextEditor;